import React, { useEffect, useMemo, useState } from 'react';
import Button from '../Common/Button';
import { MODE_VERIFIED, MODE_VERIFIED_AUTOFIX, MODE_SKETCH } from './modes';
import './FareCard.css';

const MODE_COPY = {
  [MODE_SKETCH]: {
    label: 'Sketch',
    blurb: 'Draft only. No inspection, no findings. Cheapest — and no receipt; the story’s memory doesn’t learn from this scene.',
  },
  [MODE_VERIFIED]: {
    label: 'Verified',
    blurb: 'Draft, then inspectors check it against your plan. You rule on every finding.',
    default: true,
  },
  [MODE_VERIFIED_AUTOFIX]: {
    label: 'Verified, auto-fix',
    blurb: 'Inspect, then regenerate up to twice against the findings before you read it.',
  },
};

/**
 * S1 — the fare card (Unit 4 build spec §C3). The price is settled before
 * anything runs: model menu with per-model estimates (B5), mode choice
 * carrying Sketch's full two-sentence honesty label, auto-fix consent
 * always showing its ceiling, and the promise line.
 */
export default function FareCard({
  planSummary, models, estimatesByModel, selectedModel, onSelectModel,
  mode, onSelectMode, onDraft, busy, error,
}) {
  const [autofixAcknowledged, setAutofixAcknowledged] = useState(false);
  useEffect(() => {
    if (mode !== MODE_VERIFIED_AUTOFIX) setAutofixAcknowledged(false);
  }, [mode]);

  const estimate = estimatesByModel?.[selectedModel];
  const canDraft = !busy && (mode !== MODE_VERIFIED_AUTOFIX || autofixAcknowledged) && !!estimate;

  const planLine = useMemo(() => {
    if (!planSummary) return null;
    const { beatCount, ruleCount, factCount, wordTarget } = planSummary;
    const parts = [
      `${beatCount} story point${beatCount === 1 ? '' : 's'}`,
      `${ruleCount} story rule${ruleCount === 1 ? '' : 's'}`,
      `${factCount} established fact${factCount === 1 ? '' : 's'}`,
    ];
    const target = wordTarget ? ` Target length ${wordTarget[0].toLocaleString()}–${wordTarget[1].toLocaleString()} words.` : '';
    return `Your plan for this scene carries ${parts.join(', ')}.${target}`;
  }, [planSummary]);

  return (
    <div className="er-farecard">
      <div className="er-farecard__main">
        <div className="er-farecard__intro">
          <div className="er-farecard__eyebrow">Before drafting</div>
          {planLine && <p className="er-farecard__plan">{planLine}</p>}
        </div>

        <fieldset className="er-farecard__group">
          <legend className="er-farecard__group-label">Which model writes</legend>
          <div className="er-farecard__options" role="radiogroup" aria-label="Which model writes">
            {models.map((m) => {
              const est = estimatesByModel?.[m.id];
              return (
                <label
                  key={m.id}
                  className={`er-farecard__option${selectedModel === m.id ? ' er-farecard__option--selected' : ''}`}
                >
                  <input
                    type="radio" name="er-model" value={m.id}
                    checked={selectedModel === m.id}
                    onChange={() => onSelectModel(m.id)}
                  />
                  <span className="er-farecard__option-dot" aria-hidden="true" />
                  <span className="er-farecard__option-name">{m.name}</span>
                  <span className="er-farecard__option-desc">{m.description}</span>
                  <span className="er-farecard__option-price">
                    {est && !est.missing_pricing ? `est. $${est.estimate_usd.toFixed(2)}` : '—'}
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>

        <fieldset className="er-farecard__group">
          <legend className="er-farecard__group-label">How closely it&rsquo;s inspected</legend>
          <div className="er-farecard__options" role="radiogroup" aria-label="How closely it's inspected">
            {[MODE_SKETCH, MODE_VERIFIED, MODE_VERIFIED_AUTOFIX].map((m) => (
              <label
                key={m}
                className={`er-farecard__option${mode === m ? ' er-farecard__option--selected' : ''}${m === MODE_VERIFIED_AUTOFIX ? ' er-farecard__option--stacked' : ''}`}
              >
                <span className="er-farecard__option-row">
                  <input type="radio" name="er-mode" value={m} checked={mode === m} onChange={() => onSelectMode(m)} />
                  <span className="er-farecard__option-dot" aria-hidden="true" />
                  <span className="er-farecard__option-name">{MODE_COPY[m].label}</span>
                  <span className="er-farecard__option-desc">{MODE_COPY[m].blurb}</span>
                  {MODE_COPY[m].default && <span className="er-farecard__default-tag">Default</span>}
                </span>
                {m === MODE_VERIFIED_AUTOFIX && mode === MODE_VERIFIED_AUTOFIX && (
                  <div className="er-farecard__consent">
                    A second pass may run without asking you. You&rsquo;ll still rule on the result, and
                    the draft will show what changed.{' '}
                    <span className="er-farecard__consent-ceiling">
                      Not to exceed{' '}
                      {estimate && !estimate.missing_pricing ? `$${estimate.not_to_exceed_usd.toFixed(2)}` : '—'}{' '}
                      for this scene.
                    </span>
                    <label className="er-farecard__consent-check">
                      <input
                        type="checkbox" checked={autofixAcknowledged}
                        onChange={(e) => setAutofixAcknowledged(e.target.checked)}
                      />
                      I understand a second pass may run automatically.
                    </label>
                  </div>
                )}
              </label>
            ))}
          </div>
        </fieldset>
        {error && <div className="er-farecard__error" role="alert">{error}</div>}
      </div>

      <div className="er-farecard__estimate">
        <div className="er-farecard__estimate-head">
          <span className="er-farecard__estimate-label">Estimate</span>
          <span className="er-farecard__estimate-scope">One scene</span>
        </div>
        <div className="er-farecard__estimate-amount">
          <span className="er-farecard__estimate-figure">
            {estimate && !estimate.missing_pricing ? `$${estimate.estimate_usd.toFixed(2)}` : '—'}
          </span>
          <span className="er-farecard__estimate-currency">USD</span>
        </div>
        <div className="er-farecard__rule" />
        <div className="er-farecard__estimate-lines">
          <div className="er-farecard__estimate-line">
            <span>Writing</span>
            <span>{models.find((m) => m.id === selectedModel)?.name}</span>
          </div>
          <div className="er-farecard__estimate-line">
            <span>Inspection</span>
            <span>{MODE_COPY[mode].label}</span>
          </div>
          <div className="er-farecard__estimate-line">
            <span>Not to exceed</span>
            <span>{estimate && !estimate.missing_pricing ? `$${estimate.not_to_exceed_usd.toFixed(2)}` : '—'}</span>
          </div>
        </div>
        <div className="er-farecard__rule" />
        <div className="er-farecard__cta">
          <Button variant="primary" size="large" disabled={!canDraft} loading={busy} onClick={onDraft}>
            Draft this scene
          </Button>
          <p className="er-farecard__promise">
            You&rsquo;ll read it before anything is saved to the manuscript. A receipt records what ran and what it cost.
          </p>
        </div>
      </div>
    </div>
  );
}
